import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { authData } from "../../../components/GetAuth";

const SEARCH_DEBOUNCE_MS = 400;

/**
 * Server-side paginated report data.
 *
 * const report = useServerReport("report/driver-report", { type: "all" });
 *
 * Every change to page / rows-per-page / search / date range / extra filters
 * triggers exactly one request; in-flight requests are aborted when the query
 * changes. `fetchAll()` returns every row matching the current filters (used
 * by the CSV / PDF exports).
 */
const useServerReport = (endpoint, initialFilters = {}) => {
    const auth = authData();
    const API_TOKEN = auth?.api_token;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [search, setSearchState] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [dateRange, setDateRangeState] = useState(null);
    const [filters, setFiltersState] = useState(initialFilters);

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const buildParams = useCallback(() => ({
        search: debouncedSearch,
        start_date: dateRange ? dayjs(dateRange[0]).format("YYYY-MM-DD") : null,
        end_date: dateRange ? dayjs(dateRange[1]).format("YYYY-MM-DD") : null,
        ...filters,
    }), [debouncedSearch, dateRange, filters]);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);

        axios.get(process.env.REACT_APP_API_URL + "v1/" + endpoint, {
            headers: {
                Authorization: `Bearer ${API_TOKEN}`,
            },
            params: {
                ...buildParams(),
                page: page + 1,
                per_page: rowsPerPage,
            },
            signal: controller.signal,
        }).then((response) => {
            if (response.data.status === "SUCCESS") {
                setRows(response.data.data.data || []);
                setTotal(response.data.data.total || 0);
            }
            setLoading(false);
            setInitialLoading(false);
        }).catch((error) => {
            if (axios.isCancel(error)) return;
            setRows([]);
            setTotal(0);
            setLoading(false);
            setInitialLoading(false);
        });

        return () => controller.abort();
    }, [endpoint, API_TOKEN, page, rowsPerPage, buildParams]);

    const fetchAll = useCallback(() => {
        return axios.get(process.env.REACT_APP_API_URL + "v1/" + endpoint, {
            headers: {
                Authorization: `Bearer ${API_TOKEN}`,
            },
            params: {
                ...buildParams(),
                page: 1,
                per_page: Math.max(total, 1),
            },
        }).then((response) => (response.data.status === "SUCCESS" ? response.data.data.data || [] : []));
    }, [endpoint, API_TOKEN, buildParams, total]);

    const setDateRange = (value) => {
        setDateRangeState(value);
        setPage(0);
    };

    const setFilter = (key, value) => {
        setFiltersState((prev) => ({ ...prev, [key]: value }));
        setPage(0);
    };

    const changePage = (event, newPage) => setPage(newPage);

    const changeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return {
        rows,
        total,
        loading,
        initialLoading,
        page,
        rowsPerPage,
        search,
        setSearch: setSearchState,
        dateRange,
        setDateRange,
        filters,
        setFilter,
        changePage,
        changeRowsPerPage,
        fetchAll,
    };
};

export default useServerReport;
